import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitCalendarWidgets } from '~/config/widgets/cockpit/cockpitCalendarWidgets';
import { useAuthz } from '~/context/AuthzContext';
import {
  fmtDate,
  fmtTime,
  Kpi,
  PageHead,
  Privacy,
  V3Page,
} from '~/components/cockpit/v3';
import {
  groupByDay,
  type CockpitEvent,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR,
)({
  component: CalendarPage,
});

function CalendarPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_CALENDAR,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitCalendarWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const upcoming: CockpitEvent[] =
    wp.cockpitCalendarEventsUpcomingWidget?.data ?? [];
  const past: CockpitEvent[] = wp.cockpitCalendarEventsPastWidget?.data ?? [];
  const booked = upcoming.filter((e) => e.booked).length;

  const agenda = (events: CockpitEvent[]): React.ReactElement[] =>
    groupByDay(events).map(
      ([
        day,
        list,
      ]) => (
        <div
          className="agd"
          key={day}
        >
          <h3>{fmtDate(day)}</h3>
          {list.map((e, i) => (
            <div
              className={`ag ${e.booked ? '' : 'free'}`}
              key={`${e.date}-${i}`}
            >
              <div className="agm">
                <div className="agt">
                  <b className="num">{fmtTime(e.date) || '–'}</b>
                </div>
                <div className="agb">
                  <div className="agw1">
                    <span className={`chip ${e.booked ? 'c-ok' : 'c-neu'}`}>
                      {e.booked
                        ? t('cockpit_cal_booked', 'Gebucht')
                        : t('cockpit_cal_free', 'Frei')}
                    </span>
                    {e.hostedBy ? (
                      <span className="mu sm">
                        {e.hostedBy === 'internal'
                          ? t('cockpit_cal_host_int', 'Agent Selly')
                          : t('cockpit_cal_host_ext', 'Partner')}
                      </span>
                    ) : null}
                  </div>
                  {e.bookedBy ? <span>{e.bookedBy}</span> : null}
                  {isAdvisor && e.bookedByEmail ? (
                    <a
                      className="lnk"
                      href={`mailto:${e.bookedByEmail}`}
                    >
                      {e.bookedByEmail}
                    </a>
                  ) : null}
                  {isAdvisor && e.bookedByPhone ? (
                    <a
                      className="lnk"
                      href={`tel:${e.bookedByPhone}`}
                    >
                      {e.bookedByPhone}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    );

  return (
    <V3Page>
      <PageHead
        title={t('cockpit_cal_title', 'Besichtigungen')}
        sub={t('cockpit_cal_sub', 'Ihre Termine im Überblick.')}
      />
      <div className="cols">
        <div className="stack">
          <section
            className="card"
            data-widget="cockpitCalendarEventsWidget"
          >
            <div className="sec">
              <h2>{t('cockpit_cal_upcoming', 'Kommende Termine')}</h2>
            </div>
            {upcoming.length ? (
              agenda(upcoming)
            ) : (
              <p className="mu">
                {t('cockpit_cal_none', 'Keine kommenden Termine.')}
              </p>
            )}
          </section>
          {past.length ? (
            <section className="card">
              <div className="sec">
                <h2>{t('cockpit_cal_past', 'Vergangene Termine')}</h2>
              </div>
              {agenda(past)}
            </section>
          ) : null}
        </div>
        <aside className="side">
          <div className="grid g2">
            <Kpi
              value={booked}
              label={t('cockpit_cal_kpi_booked', 'Gebucht')}
            />
            <Kpi
              value={upcoming.length - booked}
              label={t('cockpit_cal_kpi_free', 'Frei')}
            />
          </div>
          {isAdvisor ? null : (
            <Privacy>
              {t(
                'cockpit_cal_privacy',
                'Kontaktdaten der Besucher sehen nur Ihr Berater und Ihr Team.',
              )}
            </Privacy>
          )}
        </aside>
      </div>
    </V3Page>
  );
}
